import React from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { OnBoardingTask } from '../../../store/user/types';
import { GettingStartedUtils } from '../../../utils';
import { TasksList } from './TasksList';
import { TaskDetails } from './TaskDetails';
import { HomePageSection } from '../HomePageSection';
import { RegularCardBox } from '../../Cards/RegularCardBox';

interface SetupGuideSectionProps {
  tasks: OnBoardingTask[];
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    flexContainer: {
      display: 'flex',
      minHeight: 230,
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    sectionHeaderWrapper: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0.5, 0),
    },
  }),
);

export const SetupGuideSection: React.FC<SetupGuideSectionProps> = ({
  tasks,
}) => {
  const classes = useStyles();

  const initialSelectedTask = tasks.find(t => !t.fields.done);
  const [activeTaskCode, setActiveTaskCode] = React.useState<
    string | undefined
  >(initialSelectedTask?.fields.taskCode);
  const todoTasksCount = tasks.filter(task => !task.fields.done).length;

  const activeTaskDetails = React.useMemo(
    () =>
      activeTaskCode
        ? {
            ...GettingStartedUtils.MapTaskCodeToTaskDetails(
              activeTaskCode,
              true,
            ),
            path: GettingStartedUtils.MapTaskCodeToRoutePath(
              activeTaskCode,
              true,
            ),
            illustrationName: GettingStartedUtils.MapTaskCodeToIllustrationName(
              activeTaskCode,
            ),
          }
        : null,
    [activeTaskCode],
  );

  React.useEffect(() => {
    setActiveTaskCode(initialSelectedTask?.fields.taskCode);
  }, [initialSelectedTask]);

  const handleSelectTask = (code: string) => {
    setActiveTaskCode(code);
  };

  // return null when all tasks are done in order to show the empty state
  if (!todoTasksCount || !activeTaskDetails || !activeTaskCode) {
    return null;
  }

  return (
    <HomePageSection
      title="Setup guide"
      subTitle={`${todoTasksCount} step${todoTasksCount > 1 ? 's' : ''} left`}
    >
      <RegularCardBox>
        <div className={classes.flexContainer}>
          <TasksList
            tasks={tasks}
            activeTaskCode={activeTaskCode}
            onSelect={handleSelectTask}
          />
          <TaskDetails taskData={activeTaskDetails} />
        </div>
      </RegularCardBox>
    </HomePageSection>
  );
};
